import { useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { SentinelAnalyticsSeriesPoint } from '@/types/sentinelAnalytics';
import { ChartTooltip } from './chart-tooltip';
import { formatCardPercent, formatCount, formatDuration } from './format';
import { trendRows, type SeriesValue } from './series-values';

interface TrendChartProps {
  series: SentinelAnalyticsSeriesPoint[];
  /** Value for one day; null days are bridged by the line. */
  value: SeriesValue;
  unit: 'percent' | 'minutes' | 'count';
  label: string;
  /** Optional larger series drawn behind the main one (all tickets behind in-scope); the fills overlap like in Figma. */
  background?: { value: SeriesValue; label: string };
  /** Dot each day with data when the line bridges empty days, so the bridge never reads as data. */
  markDays?: boolean;
}

const TICK = { fontSize: 10, fill: 'var(--color-gray-800)' };

/** Round y ticks for a minutes axis: minute steps under 2 h, whole-hour steps above. */
function durationTicks(max: number): number[] {
  const unit = max >= 120 ? 60 : 1;
  const steps = unit === 60 ? [1, 2, 3, 4, 6, 12, 24, 48, 96, 168, 336, 720] : [1, 2, 5, 10, 15, 20, 30];
  const step = (steps.find((candidate) => candidate * unit * 4 >= max) ?? Math.ceil(max / unit / 4)) * unit;
  return Array.from({ length: Math.max(1, Math.ceil(max / step)) + 1 }, (_, index) => index * step);
}

export function TrendChart({ series, value, unit, label, background, markDays = false }: TrendChartProps) {
  const data = useMemo(() => trendRows(series, value, background?.value), [series, value, background]);
  // A one-day range has no trend to draw, so the card shows only its number.
  if (data.length < 2) return null;
  if (data.every((point) => point.value === null)) return <p className='mt-5 flex h-[127px] items-center justify-center rounded-xl bg-gray-50 text-xs text-gray-800'>No data for this metric in this range.</p>;
  const maxMinutes = Math.max(...data.map((point) => point.value ?? 0));
  const minuteTicks = unit === 'minutes' ? durationTicks(maxMinutes) : undefined;
  const inHours = maxMinutes >= 120;
  // A no-break space keeps recharts from wrapping "40 m" onto two lines.
  const formatValue = (n: number): string => (n === 0 ? '0' : unit === 'percent' ? `${Math.round(n)}%` : unit === 'minutes' ? (inHours ? `${n / 60}\u00a0h` : `${n}\u00a0m`) : n.toLocaleString());
  const tooltipValue = (n: number): string => (unit === 'percent' ? formatCardPercent(n / 100) : unit === 'minutes' ? formatDuration(n * 60_000) : formatCount(n));
  const bridged = markDays && data.some((point) => point.value === null);
  // Recharts hands the area's 0.12 fillOpacity down to dots, so each dot sets its own.
  const dot = ({ index, cx, cy }: { index: number; cx: number; cy: number }) => {
    const point = data[index];
    if (!point || point.value === null || (!bridged && !point.lone)) return <g key={index} />;
    return <circle key={index} cx={cx} cy={cy} r={point.lone ? 4 : 2.5} fill='var(--color-indigo-600)' fillOpacity={1} />;
  };
  const mainColor = background ? 'var(--color-indigo-700)' : 'var(--color-indigo-600)';
  return (
    <div className='mt-5 h-[127px]' role='img' aria-label={`${label} trend`}>
      <ResponsiveContainer width='100%' height='100%'>
        <AreaChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid stroke='var(--color-gray-100)' vertical={false} />
          <XAxis dataKey='label' tick={{ ...TICK, fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={32} tickMargin={9} height={25} padding={{ left: 11, right: 11 }} interval='preserveStartEnd' />
          <YAxis
            width={40}
            tick={TICK}
            tickLine={false}
            axisLine={false}
            tickMargin={6}
            tickFormatter={formatValue}
            domain={unit === 'percent' ? [0, 100] : minuteTicks ? [0, minuteTicks.at(-1) ?? 'auto'] : [0, 'auto']}
            ticks={unit === 'percent' ? [0, 25, 50, 75, 100] : minuteTicks}
            allowDecimals={false}
          />
          <Tooltip
            isAnimationActive={false}
            filterNull={false}
            cursor={{ stroke: 'var(--color-ink-strong)', strokeWidth: 0.5 }}
            content={<ChartTooltip format={tooltipValue} rows={[{ key: 'value', label, swatch: 'bg-indigo-700' }, ...(background ? [{ key: 'background', label: background.label, swatch: 'bg-indigo-200' }] : [])]} />}
          />
          {background ? <Area type='monotone' dataKey='background' stroke='var(--color-indigo-200)' fill='var(--color-indigo-600)' fillOpacity={0.12} strokeWidth={2} activeDot={false} connectNulls isAnimationActive={false} /> : null}
          <Area type='monotone' dataKey='value' stroke={mainColor} fill='var(--color-indigo-600)' fillOpacity={0.12} strokeWidth={2} dot={dot} activeDot={{ r: 4, fill: 'var(--color-ink-strong)', strokeWidth: 0 }} connectNulls isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
